class ProstSwitcher extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-switcher-trigger]');
    this.panel = this.querySelector('[data-switcher-panel]');
    if (!this.trigger || !this.panel) return;
    this.listeners?.abort();
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal };
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && !this.panel.hidden) {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        this.trigger.focus();
      }
    };
    this.trigger.addEventListener('click', () => this.toggle(), options);
    document.addEventListener('click', this.onDocumentClick, options);
    this.addEventListener('keydown', this.onKeydown, options);
  }

  disconnectedCallback() {
    this.listeners?.abort();
  }

  toggle() {
    this.panel.hidden ? this.open() : this.close();
  }

  open() {
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.panel.querySelector('a')?.focus();
  }

  close() {
    this.panel.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('prost-switcher')) customElements.define('prost-switcher', ProstSwitcher);

class ProstMenuDrawer extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-drawer-trigger]');
    this.panel = this.querySelector('[data-drawer-panel]');
    if (!this.trigger || !this.panel) return;
    this.listeners?.abort();
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal };
    this.trigger.addEventListener('click', () => this.toggle(), options);
    this.panel.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close], a')) this.close();
      if (event.target === this.panel) {
        const bounds = this.panel.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.close();
      }
    }, options);
    this.panel.addEventListener('close', () => {
      this.querySelectorAll('prost-switcher').forEach((switcher) => switcher.close());
      this.trigger.setAttribute('aria-expanded', 'false');
    }, options);
  }

  disconnectedCallback() {
    this.close();
    this.listeners?.abort();
  }

  toggle() {
    this.panel.open ? this.close() : this.open();
  }

  open() {
    this.panel.showModal();
    this.trigger.setAttribute('aria-expanded', 'true');
    this.panel.querySelector('[data-drawer-close]')?.focus();
  }

  close() {
    if (this.panel?.open) this.panel.close();
  }
}

if (!customElements.get('prost-menu-drawer')) customElements.define('prost-menu-drawer', ProstMenuDrawer);

class ProstReservationModal extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('dialog');
    this.form = this.querySelector('[data-reservation-form]');
    if (!this.panel || !this.form) return;
    this.date = this.form.querySelector('[data-reservation-date]');
    this.time = this.form.querySelector('[data-reservation-time]');
    this.dateTime = this.form.querySelector('[data-reservation-datetime]');
    this.listeners?.abort();
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal };
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-prost-reservation]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      if (typeof this.panel.showModal !== 'function') return;
      event.preventDefault();
      // The phone drawer closes before this document-level handler runs.
      this.returnFocus = link.closest('prost-menu-drawer')?.querySelector('[data-drawer-trigger]') || link;
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Detroit', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
      const part = (type) => parts.find((item) => item.type === type).value;
      const today = `${part('year')}-${part('month')}-${part('day')}`;
      this.date.min = today;
      if (!this.date.value || this.date.value < today) this.date.value = today;
      this.panel.showModal();
      this.querySelector('[data-reservation-close]').focus();
    }, options);
    this.form.addEventListener('submit', () => {
      // Send restaurant-local wall time, without converting it to the visitor's timezone.
      this.dateTime.value = `${this.date.value}T${this.time.value}`;
    }, options);
    this.panel.addEventListener('click', (event) => {
      if (event.target.closest('[data-reservation-close]')) this.panel.close();
      if (event.target === this.panel) {
        const bounds = this.panel.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.panel.close();
      }
    }, options);
    this.panel.addEventListener('close', () => this.returnFocus?.focus(), options);
  }

  disconnectedCallback() {
    if (this.panel?.open) this.panel.close();
    this.listeners?.abort();
  }
}

if (!customElements.get('prost-reservation-modal')) customElements.define('prost-reservation-modal', ProstReservationModal);

const safeHttpUrl = (value) => {
  if (!String(value || '').trim()) return '';
  try {
    const url = new URL(value, window.location.origin);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch (_error) {
    return '';
  }
};

const createEventCard = (event) => {
  const article = document.createElement('article');
  article.className = 'prost-card';
  const body = document.createElement('div');
  body.className = 'prost-card__body';
  const date = document.createElement('p');
  date.className = 'events-feed__date';
  const start = new Date(event.start_at || event.start);
  date.textContent = Number.isNaN(start.valueOf())
    ? ''
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: event.all_day ? undefined : 'short' }).format(start);
  const heading = document.createElement('h3');
  heading.textContent = String(event.title || 'Prost event');
  body.append(date, heading);
  if (event.description_public || event.description_text) {
    const description = document.createElement('p');
    description.className = 'prost-copy';
    description.textContent = String(event.description_public || event.description_text);
    body.append(description);
  }
  const eventUrl = safeHttpUrl(event.url || event.cta?.url);
  if (eventUrl) {
    const actions = document.createElement('div');
    actions.className = 'prost-actions';
    const link = document.createElement('a');
    link.className = 'prost-button';
    link.href = eventUrl;
    link.textContent = String(event.cta?.label || 'Event details');
    actions.append(link);
    body.append(actions);
  }
  article.append(body);
  return article;
};

const loadEvents = async (section) => {
  if (section.dataset.eventsReady === 'true') return;
  const endpoint = safeHttpUrl(section.dataset.endpoint);
  if (!endpoint) return;
  section.dataset.eventsReady = 'true';
  const controller = new AbortController();
  section.eventsAbortController = controller;
  const timeout = window.setTimeout(() => controller.abort(), 3000);
  try {
    const url = new URL('v1/events', endpoint.endsWith('/') ? endpoint : `${endpoint}/`);
    const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal });
    if (!response.ok) throw new Error('Events response was not successful');
    const payload = await response.json();
    const events = Array.isArray(payload) ? payload : payload.events;
    if (!Array.isArray(events)) throw new Error('Events payload is incompatible');
    const context = section.dataset.context || document.body.dataset.prostContext;
    const upcoming = events.filter((event) => {
      const location = String(event.business || event.location || '').replace('_', '-');
      return event && event.title && (location === context || location === 'global');
    }).slice(0, 6);
    if (!upcoming.length) return;
    const list = section.querySelector('[data-events-list]');
    list.replaceChildren(...upcoming.map(createEventCard));
    const status = section.querySelector('[data-events-status]');
    if (status) status.textContent = `${upcoming.length} upcoming events loaded.`;
  } catch (_error) {
    // Merchant-managed fallbacks remain visible after network or schema failures.
  } finally {
    window.clearTimeout(timeout);
  }
};

const initializeEvents = (root = document) => root.querySelectorAll('[data-events-feed]').forEach(loadEvents);

const syncMobileActions = () => {
  document.body.classList.toggle('has-mobile-actions', Boolean(document.querySelector('.mobile-actions')));
};

document.addEventListener('DOMContentLoaded', syncMobileActions);
document.addEventListener('DOMContentLoaded', () => initializeEvents());
document.addEventListener('shopify:section:load', (event) => { syncMobileActions(); initializeEvents(event.target); });
document.addEventListener('shopify:section:unload', (event) => { event.target.querySelector('[data-events-feed]')?.eventsAbortController?.abort(); syncMobileActions(); });
